/**
 * Metafans UI Effects
 * Miscellaneous UI enhancements, tooltips, video autoplay, and global search.
 */

import { postAction } from './api.js';

export function initUIEffects() {
    initVideoAutoplay();
    initTooltips();
    initSocialSearch();
    initNotificationActions();
    initGroupCreationProgress();
    initBbPressLikeDislike();

    // Remove broken images in masonry
    document.addEventListener('error', (e) => {
        if (e.target.tagName === 'IMG' && e.target.closest('.mesonry')) {
            e.target.remove();
        }
    }, true);
}

// --- 1. Video Autoplay on Scroll ---
function initVideoAutoplay() {
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    video.play().catch(() => { });
                } else {
                    video.pause();
                }
            });
        }, { threshold: 0.5 });

        document.querySelectorAll('.activity video').forEach(video => {
            video.muted = true;
            video.setAttribute('playsinline', '');

            // Hover controls
            video.addEventListener('mouseenter', () => video.setAttribute('controls', 'controls'));
            video.addEventListener('mouseleave', () => video.removeAttribute('controls'));

            observer.observe(video);
        });
    }
}

// --- 2. Tooltips ---
function initTooltips() {
    const tooltipBox = document.createElement('div');
    tooltipBox.className = 'bp-tooltip-box';

    document.body.addEventListener('mouseover', (e) => {
        const target = e.target.closest('[data-bp-tooltip]');
        if (target) {
            tooltipBox.textContent = target.getAttribute('data-bp-tooltip');
            document.body.appendChild(tooltipBox);
            tooltipBox.style.display = 'block';

            // Initial Pos
            positionTooltip(e, tooltipBox);
        }
    });

    document.body.addEventListener('mousemove', (e) => {
        if (tooltipBox.style.display === 'block') {
            positionTooltip(e, tooltipBox);
        }
    });

    document.body.addEventListener('mouseout', (e) => {
        if (e.target.closest('[data-bp-tooltip]')) {
            tooltipBox.remove();
            tooltipBox.style.display = 'none';
        }
    });
}

function positionTooltip(e, box) {
    box.style.left = `${e.pageX}px`;
    box.style.top = `${e.pageY - box.offsetHeight - 10}px`;
}

// --- 3. Social Search (AJAX) ---
function initSocialSearch() {
    const inputs = document.querySelectorAll('.header-social_search_box-item input.search-field');

    inputs.forEach(input => {
        input.addEventListener('input', debounce(async (e) => {
            const text = e.target.value.trim();
            const resultBox = e.target.closest('.header-social_search_box-item').querySelector('.search-box-result');

            if (!resultBox) return;

            if (text === '') {
                resultBox.style.display = 'none';
                resultBox.innerHTML = '';
                return;
            }

            resultBox.style.display = 'block';
            try {
                const html = await postAction('th_bp_bb_social_search', { text });
                resultBox.innerHTML = html;
            } catch (err) {
                console.error(err);
            }
        }, 300));
    });
}

// --- 4. Notification Actions ---
function initNotificationActions() {
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('.notification-actions a');
        if (!btn) return;

        e.preventDefault();
        const action = btn.dataset.action; // 'delete' or 'read'
        const id = btn.dataset.notificationId;

        if (action === 'delete') {
            // Ideally use a confirming modal module, simplified here:
            if (confirm('Delete notification?')) {
                handleNotificationAction(action, id);
            }
        } else {
            handleNotificationAction(action, id);
        }
    });
}

async function handleNotificationAction(action, id) {
    try {
        const data = await postAction('handle_notification', {
            Action: action,
            notification_id: id
        });
        if (window.showNotification) showNotification(data.response_type, data.response_msg);
        window.location.reload();
    } catch (err) {
        console.error(err);
    }
}

// --- 5. Group Creation Progress ---
function initGroupCreationProgress() {
    const groupProgNavs = document.querySelectorAll('.group-create-buttons li');
    if (!groupProgNavs.length) return;

    // Logic from original file to restyle steps
    let currentIndex = -1;
    groupProgNavs.forEach((el, i) => {
        if (el.classList.contains('current')) currentIndex = i;
    });

    if (currentIndex >= 0) {
        groupProgNavs.forEach((el, i) => {
            const isComplete = i <= currentIndex;
            const target = el.querySelector('a') || el.querySelector('span'); // span if disabled
            if (!target) return;

            const parts = target.innerText.split('.'); // Assumes "1. Step Name" format
            if (parts.length < 2) return;

            const num = parts[0];
            const text = parts[1];

            // Chevron icon SVG string
            const chevron = (i < groupProgNavs.length - 1)
                ? '<svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>'
                : '';

            target.innerHTML = `
                <span class="nm ${isComplete ? 'cnm' : ''}">${num}</span>
                <span class="txt ${isComplete ? 'ctxt' : ''}">${text}</span>
                ${chevron}
            `;
        });
    }
}

// --- 6. BBPress Like/Dislike ---
function initBbPressLikeDislike() {
    document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.bbp-like-btn, .bbp-dislike-btn');
        if (!btn) return;

        e.preventDefault();
        const container = btn.closest('.bbp-like-dislike');
        const postId = container.dataset.post;
        const type = btn.classList.contains('bbp-like-btn') ? 'like' : 'dislike';

        // Add loading state logic if desired

        try {
            // Using jQuery because of nonce dependency in original code (metafans_local.nonce)
            // Or use postAction if you pass nonce
            const res = await jQuery.ajax({
                type: 'POST',
                url: Metafans_JS.ajaxurl,
                data: {
                    action: 'bbp_like_toggle',
                    nonce: (window.metafans_local && window.metafans_local.nonce) || '',
                    post_id: postId,
                    rtype: type
                }
            });

            if (res.success) {
                const likeBtn = container.querySelector('.bbp-like-btn');
                const dislikeBtn = container.querySelector('.bbp-dislike-btn');

                likeBtn.querySelector('span').innerText = res.data.likes;
                dislikeBtn.querySelector('span').innerText = res.data.dislikes;

                likeBtn.classList.remove('liked');
                dislikeBtn.classList.remove('disliked');

                if (type === 'like') likeBtn.classList.add('liked');
                else dislikeBtn.classList.add('disliked');
            }
        } catch (err) {
            console.error(err);
        }
    });
}

// Util
function debounce(func, wait) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}